import { useLazyQuery } from '@apollo/client'
import { useState } from 'react'
import styled from 'styled-components'

import { GET_PAGE_BACKGROUND_XML } from 'data/layoutcreator/queries/themes'
import ConfirmBackgroundDeleteModal from 'shared/components/modals/ConfirmBackgroundDeleteModal'
import ChiliVariables, {
  VariableTypes
} from 'shared/components/Tools/services/ChiliVariables'
import { useBackgroundUploads } from 'shared/hooks/useBackgroundUploads'
import { FileTypes } from 'shared/types/global'
import BackgroundItem from 'shared/ui/BackgroundItem'
import FileUpload from 'shared/ui/FileUpload'
import Flex from 'shared/ui/Flex'
import { Skeleton } from 'shared/ui/Skeleton'
import UploadZone from 'shared/ui/UploadZone'

import { BackgroundTabProps } from '../../types'

const variablesApi = ChiliVariables.getInstance()

const Container = styled(Flex)`
  flex-direction: column;
  margin-top: 32px;
  width: 100%;
  gap: 27px;
`

const ItemsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 8px;
`

const ChangeBackgroundPanelUploadsTab: React.FC<
  BackgroundTabProps
> = ({ variableName, isBackgroundHidden }) => {
  const [getBackgroundXML] = useLazyQuery(GET_PAGE_BACKGROUND_XML)

  const [isSelectedLoading, setIsSelectedLoading] = useState(false)

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    handleUploadClick,
    handleFileUpload,
    requestImageRemove,
    confirmImageRemove,
    cancelImageRemove,
    isRemoveConfirmationOpen,
    selectedImageId,
    setSelectedImageId,
    imageInputRef,
    isImagesLoading,
    images
  } = useBackgroundUploads({
    onSelectedBackgroundChange: async data => {
      if (!data) {
        return
      }

      if (isBackgroundHidden) {
        return
      }

      setIsSelectedLoading(true)

      const image = images?.find(image => image.id === data.id)

      if (image?.type === 'dap') {
        return
      } else {
        const { data: xmlData } = await getBackgroundXML({
          variables: { id: data.id }
        })

        const xml = xmlData?.getPageBackgroundXML as string

        await variablesApi.setVariableValue(variableName, xml, {
          type: VariableTypes.chiliImage
        })
      }

      setIsSelectedLoading(false)
    }
  })

  const handleImageClick = (imageId: string) => {
    const newSelectedImageId =
      imageId === selectedImageId ? null : imageId
    setSelectedImageId(newSelectedImageId)
  }

  return (
    <Container {...getRootProps()}>
      <UploadZone
        labelId="Project.settings.templates.backgrounds.modal.tabs.uploads.uploadAreaLabel"
        onClick={handleUploadClick}
        isDragActive={isDragActive}
      />

      <ItemsContainer style={{ opacity: isDragActive ? 0.3 : 1 }}>
        {isImagesLoading
          ? new Array(3 * 10)
              .fill(null)
              .map((_, i) => (
                <Skeleton key={i} width={78} height={103} />
              ))
          : images?.map(image => (
              <BackgroundItem
                key={image.id}
                width={76}
                height={103}
                isRemovable
                isSelected={image.id === selectedImageId}
                isLoading={
                  image.id === selectedImageId && isSelectedLoading
                }
                onClick={() => handleImageClick(image.id)}
                onRemove={() =>
                  requestImageRemove({
                    backgroundId: image.id,
                    type: image.type
                  })
                }
                src={new URL(
                  image.thumbPath,
                  process.env.REACT_APP_REST_URL
                ).toString()}
              />
            ))}
      </ItemsContainer>
      {images?.length === 0 && <span>No images here yet</span>}

      <FileUpload
        {...getInputProps({
          ref: imageInputRef,
          onSelect: (file: any) => handleFileUpload(file),
          type: FileTypes.image
        })}
      />

      <ConfirmBackgroundDeleteModal
        isOpen={isRemoveConfirmationOpen}
        closeModal={cancelImageRemove}
        onConfirm={confirmImageRemove}
      />
    </Container>
  )
}

export default ChangeBackgroundPanelUploadsTab
